import type { Component } from 'vue'
import createLogger from '@/utils/logger'
import HomeIcon from '@/components/customers/navigation/svgs/HomeIcon.vue'
import UserIcon from '@/components/customers/navigation/svgs/UserIcon.vue'
import CalendarIcon from '@/components/customers/navigation/svgs/CalendarIcon.vue'
import ConsultsIcon from '@/components/customers/navigation/svgs/ConsultsIcon.vue'
import ExploreIcon from '@/components/customers/navigation/svgs/ExploreIcon.vue'
import VideoIcon from '@/components/customers/navigation/svgs/VideoIcon.vue'

import type { RouteRecordRaw } from 'vue-router'
import type { RouteRecordWithMeta } from './types'

const logger = createLogger('router/customerRoutes.ts')

const CustomerRoot = () => import('@/views/customers/CustomerRoot.vue')
const HomePage = () => import('@/components/customers/pages/HomePage.vue')
const AccountPage = () => import('@/components/customers/pages/AccountPage.vue')
const SchedulePage = () => import('@/components/customers/pages/SchedulePage.vue')
const ClassPage = () => import('@/components/customers/pages/ClassPage.vue')
const InstructorProfilePage = () => import('@/components/customers/pages/InstructorProfilePage.vue')
const ExplorePage = () => import('@/components/customers/pages/ExplorePage.vue')
const RecordingsPage = () => import('@/components/customers/pages/RecordingsPage.vue')
const ConsultationsPage = () => import('@/components/customers/pages/ConsultationsPage.vue')
const RecordingPlayerPage = () => import('@/components/customers/pages/RecordingPlayerPage.vue')
const LiveClassPage = () => import('@/components/customers/pages/LiveClassPage.vue')
const ConsultationJoinPage = () => import('@/components/customers/pages/ConsultationJoinPage.vue')
const CarePlanDownloadPage = () => import('@/components/customers/pages/CarePlanDownloadPage.vue')
const SelectPlanPage = () => import('@/components/customers/pages/SelectPlanPage.vue')
const StripeCheckoutPage = () => import('@/components/customers/pages/StripeCheckoutPage.vue')
const QRcodeDownloadPage = () => import('@/components/customers/pages/QRcodeDownloadPage.vue')
const BookNewConsultationPage = () =>
  import('@/components/customers/pages/BookNewConsultationPage.vue')

type NavConfig = {
  icon: Component | null
  label: string
} & RouteRecordWithMeta

export const CustomerPages: Record<
  | 'Home'
  | 'Explore'
  | 'RecordingsList'
  | 'Consultations'
  | 'Schedule'
  | 'RecordingPlayer'
  | 'You'
  | 'SingleClass'
  | 'SingleClassSchedule'
  | 'InstructorProfile'
  | 'LiveClass'
  | 'ConsultationJoin'
  | 'CarePlanDownload'
  | 'SelectPlan'
  | 'Checkout'
  | 'QRcodeDownload'
  | 'BookNewConsultation',
  NavConfig
> = {
  Home: {
    label: 'Home',
    name: 'home',
    icon: HomeIcon,
    path: '/home',
    component: HomePage,
    meta: {
      requiresAuth: true,
      showInNavbar: true,
      isCustomer: true,
      hideNavbar: true,
      isSignup: false,
      showFilters: false,
    },
  },
  Schedule: {
    label: 'Schedule',
    name: 'schedule',
    icon: CalendarIcon,
    path: '/schedule',
    component: SchedulePage,
    meta: {
      requiresAuth: true,
      showInNavbar: true,
      isCustomer: true,
      hideNavbar: true,
      isSignup: false,
      showFilters: false,
    },
  },
  RecordingsList: {
    label: 'Recordings',
    name: 'recordings-list',
    icon: VideoIcon,
    path: '/recordings-list',
    component: RecordingsPage,
    meta: {
      requiresAuth: true,
      showInNavbar: true,
      isCustomer: true,
      hideNavbar: true,
      isSignup: false,
      showFilters: false,
    },
  },
  Consultations: {
    label: 'Consults',
    name: 'consultations',
    icon: ConsultsIcon,
    path: '/consultations',
    component: ConsultationsPage,
    meta: {
      requiresAuth: true,
      showInNavbar: true,
      isCustomer: true,
      hideNavbar: true,
      isSignup: false,
      showFilters: false,
      consultation: true,
    },
  },
  RecordingPlayer: {
    label: 'Recording Player',
    name: 'recording-player/:classInstanceId',
    icon: null,
    path: '/recording-player/:classInstanceId',
    component: RecordingPlayerPage,
    meta: {
      requiresAuth: true,
      showInNavbar: false,
      isCustomer: true,
      hideNavbar: true,
      isSignup: false,
      showFilters: false,
      hideTopBar: true,
    },
  },
  Explore: {
    label: 'Explore',
    name: 'explore',
    icon: ExploreIcon,
    path: '/explore',
    component: ExplorePage,
    meta: {
      requiresAuth: true,
      showInNavbar: true,
      isCustomer: true,
      hideNavbar: true,
      isSignup: false,
      showFilters: false,
    },
  },
  You: {
    label: 'Account',
    name: 'account',
    icon: UserIcon,
    path: '/account',
    component: AccountPage,
    meta: {
      requiresAuth: true,
      showInNavbar: true,
      isCustomer: true,
      hideNavbar: true,
      isSignup: false,
      showFilters: false,
    },
  },
  SingleClass: {
    label: 'Class',
    name: 'class/:id',
    icon: null,
    path: '/class/:id',
    component: ClassPage,
    meta: {
      requiresAuth: true,
      showInNavbar: false,
      isCustomer: true,
      hideNavbar: true,
      isSignup: false,
      showFilters: false,
    },
  },
  SingleClassSchedule: {
    label: 'Class Schedule',
    name: 'class/:id/schedule/:scheduleId',
    icon: null,
    path: '/class/:id/schedule/:scheduleId',
    component: ClassPage,
    meta: {
      requiresAuth: true,
      showInNavbar: false,
      isCustomer: true,
      hideNavbar: true,
      isSignup: false,
      showFilters: false,
    },
  },
  InstructorProfile: {
    label: 'Instructor Profile',
    name: 'instructor/:id',
    icon: null,
    path: '/instructor/:id',
    component: InstructorProfilePage,
    meta: {
      requiresAuth: true,
      showInNavbar: false,
      isCustomer: true,
      hideNavbar: true,
      isSignup: false,
      showFilters: false,
    },
  },
  LiveClass: {
    label: 'Live Class',
    name: 'live-class-view/:id',
    icon: null,
    path: '/live-class-view/:id',
    component: LiveClassPage,
    meta: {
      requiresAuth: true,
      showInNavbar: false,
      isCustomer: true,
      hideNavbar: true,
      hideFooter: true,
      isSignup: false,
      showFilters: false,
      hideCustomerNavbar: true,
      hideTopBar: true,
    },
  },
  ConsultationJoin: {
    label: 'Consultation Join',
    name: 'consultation-view/:id',
    icon: null,
    path: '/consultation-view/:id',
    component: ConsultationJoinPage,
    meta: {
      requiresAuth: true,
      showInNavbar: false,
      isCustomer: true,
      hideNavbar: true,
      hideFooter: true,
      isSignup: false,
      showFilters: false,
      hideCustomerNavbar: true,
      hideTopBar: true,
      consultation: true,
    },
  },
  CarePlanDownload: {
    label: 'Care Plan',
    name: 'care-plan/:consultationId',
    icon: null,
    path: '/care-plan/:consultationId',
    component: CarePlanDownloadPage,
    meta: {
      requiresAuth: true,
      showInNavbar: false,
      isCustomer: true,
      hideNavbar: true,
      hideFooter: true,
      isSignup: false,
      showFilters: false,
      hideCustomerNavbar: true,
      hideTopBar: true,
    },
  },
  SelectPlan: {
    label: 'Select Plan',
    name: 'select-plan',
    icon: null,
    path: '/select-plan',
    component: SelectPlanPage,
    meta: {
      requiresAuth: true,
      showInNavbar: false,
      isCustomer: true,
      hideNavbar: true,
      isSignup: false,
      showFilters: false,
    },
  },
  Checkout: {
    path: '/checkout/:productId',
    name: 'checkout',
    component: StripeCheckoutPage,
    icon: null,
    label: 'Checkout',
    meta: {
      requiresAuth: true,
      hideNavbar: true,
      isCustomer: true,
      isSignup: false,
      showInNavbar: false,
      showFilters: false,
    },
  },
  QRcodeDownload: {
    path: '/qrcode-download',
    name: 'qrcode-download',
    component: QRcodeDownloadPage,
    icon: null,
    label: 'QR Code Download',
    meta: {
      requiresAuth: false,
      hideNavbar: true,
      isCustomer: true,
      isSignup: false,
      showInNavbar: false,
      showFilters: false,
      hideCustomerNavbar: true,
      hideTopBar: true,
    },
  },
  BookNewConsultation: {
    path: '/book-new-consultation',
    name: 'book-new-consultation',
    component: BookNewConsultationPage,
    icon: null,
    label: 'Book Coach Consultation',
    meta: {
      requiresAuth: true,
      hideNavbar: true,
      isCustomer: true,
      isSignup: false,
      showInNavbar: false,
      showFilters: false,
      hideCustomerNavbar: true,
      hideTopBar: true,
    },
  },
}

export const CUSTOMER_PAGES_LIST = Object.values(CustomerPages)

/**
 * Route `name` → lazy `component` for every `meta.showInNavbar` customer route.
 * Built from `CustomerPages` so renames stay in sync with the router.
 */
function buildCustomerNavbarRouteLoadersByName(
  pages: Record<string, NavConfig>,
): Record<string, () => Promise<unknown>> {
  const out: Record<string, () => Promise<unknown>> = {}

  for (const page of Object.values(pages)) {
    if (!page.meta.showInNavbar) {
      continue
    }
    const name = page.name
    if (name == null || typeof name !== 'string') {
      if (import.meta.env.DEV) {
        logger.warn(`showInNavbar route needs a string \`name\` for prefetch (path: ${page.path})`)
      }
      continue
    }
    const { component } = page
    if (typeof component !== 'function') {
      if (import.meta.env.DEV) {
        logger.warn(`showInNavbar route "${name}" has no dynamic import(); prefetch will skip it.`)
      }
      continue
    }
    out[name] = component as () => Promise<unknown>
  }

  return out
}

export const CUSTOMER_ROUTE_LOADERS_BY_NAME = buildCustomerNavbarRouteLoadersByName(CustomerPages)

/** Exported for shell prefetch only (`routePrefetch.ts`). */
export { CustomerRoot }

export const CUSTOMER_ROUTES_GROUP: RouteRecordRaw = {
  path: '/',
  component: CustomerRoot,
  children: Object.values(CustomerPages) as RouteRecordRaw[],
}
